// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { buildEvent, validateSplitsBalance } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import {
  calculateCostBasis,
  calculateRealizedGainFromProceeds,
  getCommodityId,
  parseShares,
  parsePrice,
  SHARE,
} from '../../lib/investment.js';
import type { Split, TransactionPostedPayload, PriceAddedPayload } from '../../types.js';

type TxType = 'buy' | 'sell' | 'div' | 'rdiv' | 'return_capital' | 'custom';

interface InvestmentTransactionFormProps {
  investmentAccountId: string;
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  onClose: () => void;
}

const inputCls = 'bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle w-full';
const labelCls = 'block text-xs text-text-muted mb-1';

function parseCents(str: string): number {
  try {
    if (!/^[\d.+\-*/() ]+$/.test(str)) return 0;
    const result = Function('"use strict"; return (' + str + ')')() as number;
    if (typeof result !== 'number' || isNaN(result)) return 0;
    return Math.round(result * 100);
  } catch {
    return 0;
  }
}

export function InvestmentTransactionForm({
  investmentAccountId,
  worker,
  keyData,
  onClose,
}: InvestmentTransactionFormProps) {
  const { t } = useTranslation(['investment', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const investmentAccount = ledger.accounts.get(investmentAccountId);
  const today = new Date().toISOString().slice(0, 10);

  const [txType, setTxType] = useState<TxType>('buy');
  const [date, setDate] = useState(today);
  const [sharesStr, setSharesStr] = useState('');
  const [priceStr, setPriceStr] = useState('');
  const [totalStr, setTotalStr] = useState('');
  const [commissionStr, setCommissionStr] = useState('');
  const [cashAccountId, setCashAccountId] = useState('');
  const [capGainsAccountId, setCapGainsAccountId] = useState('');
  const [incomeAccountId, setIncomeAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [estimatedGain, setEstimatedGain] = useState<{ gainLoss: number; term: string } | null>(null);

  const accounts = useMemo(() => [...ledger.accounts.values()].filter((a) => !a.placeholder), [ledger.accounts]);
  const cashAccounts = useMemo(
    () => accounts.filter((a) => ['BANK', 'CASH', 'ASSET'].includes(a.type)),
    [accounts],
  );
  const incomeAccounts = useMemo(
    () => accounts.filter((a) => a.type === 'INCOME'),
    [accounts],
  );
  const expenseAccounts = useMemo(
    () => accounts.filter((a) => a.type === 'EXPENSE'),
    [accounts],
  );

  // Auto-fill total when shares and price both entered
  useEffect(() => {
    const shares = parseShares(sharesStr);
    const price = parsePrice(priceStr);
    if (shares > 0 && price > 0 && !totalStr) {
      const total = Math.round((shares * price) / SHARE);
      setTotalStr((total / 100).toFixed(2));
    }
  }, [sharesStr, priceStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute estimated gain/loss for sell transactions in real time
  useEffect(() => {
    if (txType !== 'sell') { setEstimatedGain(null); return; }
    const shares = parseShares(sharesStr);
    const total = parseCents(totalStr);
    const commission = parseCents(commissionStr);
    if (shares <= 0 || total <= 0) { setEstimatedGain(null); return; }

    const allTxs = [...ledger.transactions.values()];
    const lots = calculateCostBasis(investmentAccountId, allTxs);
    const grossProceeds = total; // total entered = gross proceeds
    const result = calculateRealizedGainFromProceeds(lots, shares, grossProceeds - commission, date);
    const term = result.isLongTerm && !result.isShortTerm
      ? 'long-term'
      : result.isShortTerm && !result.isLongTerm
      ? 'short-term'
      : 'mixed';
    setEstimatedGain({ gainLoss: result.gainLoss, term });
  }, [sharesStr, totalStr, commissionStr, date, txType, investmentAccountId, ledger.transactions]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!date) { setError(t('txForm.dateRequired')); return; }
    if (!investmentAccount) { setError(t('txForm.accountNotFound')); return; }

    const shares = parseShares(sharesStr);
    const total = parseCents(totalStr);
    const commission = parseCents(commissionStr);
    const currency = investmentAccount.currency;
    const commodityId = getCommodityId(investmentAccount);
    const priceInCents = parsePrice(priceStr);

    let splits: Split[] = [];
    const txId = uuidv4();

    try {
      if (txType === 'buy') {
        if (!cashAccountId) { setError(t('txForm.cashRequiredBuy')); return; }
        if (shares <= 0) { setError(t('txForm.sharesPositive')); return; }
        if (total <= 0) { setError(t('txForm.totalPositive')); return; }

        // Buy: Cash out (total + commission), Investment in (total, qty: +shares), Commission expense
        splits = [
          {
            splitId: uuidv4(), accountId: cashAccountId,
            amount: -(total + commission), quantity: -(total + commission),
            reconcileState: 'n', reconcileDate: null, memo, action: '',
          },
          {
            splitId: uuidv4(), accountId: investmentAccountId,
            amount: total, quantity: shares,
            reconcileState: 'n', reconcileDate: null, memo, action: 'Buy',
          },
          ...(commission > 0 && expenseAccounts.length > 0
            ? [{
                splitId: uuidv4(),
                accountId: expenseAccounts.find((a) => /commission/i.test(a.name))?.id ?? expenseAccounts[0]!.id,
                amount: commission, quantity: commission,
                reconcileState: 'n' as const, reconcileDate: null, memo: 'Commission', action: '',
              }]
            : []),
        ];

      } else if (txType === 'sell') {
        if (!cashAccountId) { setError(t('txForm.cashRequiredSell')); return; }
        if (shares <= 0) { setError(t('txForm.sharesPositive')); return; }
        if (total <= 0) { setError(t('txForm.proceedsPositive')); return; }

        const allTxs = [...ledger.transactions.values()];
        const lots = calculateCostBasis(investmentAccountId, allTxs);

        // Compute how much cost basis is being removed (FIFO)
        let remaining = shares;
        let basisRemoved = 0;
        for (const lot of lots) {
          if (remaining <= 0) break;
          const consumed = Math.min(lot.remainingShares, remaining);
          basisRemoved += Math.round((consumed * lot.costPerShare) / SHARE);
          remaining -= consumed;
        }

        // Net cash = total - commission (commission comes out of proceeds)
        const netCash = total - commission;
        // Gross gain = gross proceeds - commission - cost basis
        // We record: Cash: +netCash, Investment: -basisRemoved (qty:-shares),
        // Commission: +commission, Capital Gains: -(netCash - basisRemoved)
        // Simplify: capitalGainsAmount = -(total - basisRemoved)? No...
        // Double-entry: netCash + (-basisRemoved) + commission + capGains = 0
        // capGains = -(netCash - basisRemoved + commission) = -(total - commission - basisRemoved + commission) = -(total - basisRemoved)
        const capGains = -(total - basisRemoved);

        splits = [
          {
            splitId: uuidv4(), accountId: cashAccountId,
            amount: netCash, quantity: netCash,
            reconcileState: 'n', reconcileDate: null, memo, action: '',
          },
          {
            splitId: uuidv4(), accountId: investmentAccountId,
            amount: -basisRemoved, quantity: -shares,
            reconcileState: 'n', reconcileDate: null, memo, action: 'Sell',
          },
          ...(commission > 0
            ? [{
                splitId: uuidv4(),
                accountId: expenseAccounts.find((a) => /commission/i.test(a.name))?.id ?? expenseAccounts[0]?.id ?? cashAccountId,
                amount: commission, quantity: commission,
                reconcileState: 'n' as const, reconcileDate: null, memo: 'Commission', action: '',
              }]
            : []),
          ...(capGainsAccountId
            ? [{
                splitId: uuidv4(), accountId: capGainsAccountId,
                amount: capGains, quantity: capGains,
                reconcileState: 'n' as const, reconcileDate: null, memo: 'Capital gain/loss', action: '',
              }]
            : []),
        ];

      } else if (txType === 'div') {
        if (!cashAccountId) { setError(t('txForm.cashRequiredDividend')); return; }
        if (total <= 0) { setError(t('txForm.dividendPositive')); return; }
        const incomeAcc = incomeAccountId || incomeAccounts[0]?.id;
        if (!incomeAcc) { setError(t('txForm.incomeRequiredDividend')); return; }

        splits = [
          { splitId: uuidv4(), accountId: cashAccountId, amount: total, quantity: total, reconcileState: 'n', reconcileDate: null, memo, action: '' },
          { splitId: uuidv4(), accountId: incomeAcc, amount: -total, quantity: -total, reconcileState: 'n', reconcileDate: null, memo, action: '' },
        ];

      } else if (txType === 'rdiv') {
        if (shares <= 0) { setError(t('txForm.sharesPositive')); return; }
        if (total <= 0) { setError(t('txForm.dividendPositive')); return; }
        const incomeAcc = incomeAccountId || incomeAccounts[0]?.id;
        if (!incomeAcc) { setError(t('txForm.incomeRequiredReinvested')); return; }

        splits = [
          { splitId: uuidv4(), accountId: investmentAccountId, amount: total, quantity: shares, reconcileState: 'n', reconcileDate: null, memo, action: 'ReinvDiv' },
          { splitId: uuidv4(), accountId: incomeAcc, amount: -total, quantity: -total, reconcileState: 'n', reconcileDate: null, memo, action: '' },
        ];

      } else if (txType === 'return_capital') {
        if (!cashAccountId) { setError(t('txForm.cashRequired')); return; }
        if (total <= 0) { setError(t('txForm.amountPositive')); return; }

        // Return of capital: reduce cost basis (reduce investment account)
        splits = [
          { splitId: uuidv4(), accountId: cashAccountId, amount: total, quantity: total, reconcileState: 'n', reconcileDate: null, memo, action: '' },
          { splitId: uuidv4(), accountId: investmentAccountId, amount: -total, quantity: 0, reconcileState: 'n', reconcileDate: null, memo, action: 'ReturnCapital' },
        ];
      }

      validateSplitsBalance(splits);

    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:transactionUnbalanced'));
      return;
    }

    setSaving(true);
    try {
      const payload: TransactionPostedPayload = {
        transactionId: txId,
        date,
        dateEntered: new Date().toISOString(),
        description: description.trim() || txTypeLabel(txType),
        notes: '',
        currency,
        splits,
      };
      await encryptAndPush(await buildEvent('TransactionPosted', payload, ledger.lastSequence + 1));

      // Record price in price database if we have a price per share
      if (priceInCents > 0 && commodityId && (txType === 'buy' || txType === 'sell' || txType === 'rdiv')) {
        const pricePayload: PriceAddedPayload = {
          priceId: uuidv4(),
          commodityId,
          currency,
          date,
          source: 'transaction',
          type: 'transaction',
          value: priceInCents,
        };
        await encryptAndPush(await buildEvent('PriceAdded', pricePayload, ledger.lastSequence + 2));
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:saveTransaction'));
    } finally {
      setSaving(false);
    }
  }

  function txTypeLabel(type: TxType): string {
    const keys = {
      buy: 'txForm.purchase', sell: 'txForm.sale', div: 'txForm.dividend',
      rdiv: 'txForm.reinvestedDividend', return_capital: 'txForm.returnOfCapital',
      custom: 'txForm.title',
    } as const;
    return t(keys[type]);
  }

  if (!investmentAccount) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-text">{t('txForm.titleNamed', { name: investmentAccount.name })}</h2>
            <button onClick={onClose} className="text-text-muted hover:text-text text-xl leading-none">&times;</button>
          </div>

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}

          {/* Transaction type selector */}
          <div className="flex gap-1 mb-5 flex-wrap">
            {(['buy', 'sell', 'div', 'rdiv', 'return_capital'] as TxType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTxType(t)}
                className={`px-3 py-1 text-xs font-medium border transition-colors ${
                  txType === t
                    ? 'bg-accent text-white border-accent'
                    : 'text-text-muted border-border hover:bg-bg-elevated hover:text-text'
                }`}
              >
                {txTypeLabel(t)}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{t('register.columns.date')}</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>{t('register.columns.description')}</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={txTypeLabel(txType)}
                  className={inputCls}
                />
              </div>
            </div>

            {/* Shares + Price — for buy, sell, rdiv */}
            {(txType === 'buy' || txType === 'sell' || txType === 'rdiv') && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>{t('txForm.shares')}</label>
                  <input
                    type="text"
                    value={sharesStr}
                    onChange={(e) => { setSharesStr(e.target.value); setTotalStr(''); }}
                    placeholder="0.000"
                    className={`${inputCls} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('txForm.pricePerShare')}</label>
                  <input
                    type="text"
                    value={priceStr}
                    onChange={(e) => { setPriceStr(e.target.value); setTotalStr(''); }}
                    placeholder="0.00"
                    className={`${inputCls} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelCls}>{txType === 'buy' ? t('txForm.totalCost') : t('txForm.totalProceeds')}</label>
                  <input
                    type="text"
                    value={totalStr}
                    onChange={(e) => setTotalStr(e.target.value)}
                    placeholder="0.00"
                    className={`${inputCls} font-mono`}
                  />
                </div>
              </div>
            )}

            {/* Total amount for div and return_capital */}
            {(txType === 'div' || txType === 'return_capital') && (
              <div>
                <label className={labelCls}>{t('register.columns.amount')}</label>
                <input
                  type="text"
                  value={totalStr}
                  onChange={(e) => setTotalStr(e.target.value)}
                  placeholder="0.00"
                  className={`${inputCls} font-mono`}
                />
              </div>
            )}

            {/* Commission — buy and sell */}
            {(txType === 'buy' || txType === 'sell') && (
              <div>
                <label className={labelCls}>{t('txForm.commission')} <span className="text-text-subtle">{t('txForm.optional')}</span></label>
                <input
                  type="text"
                  value={commissionStr}
                  onChange={(e) => setCommissionStr(e.target.value)}
                  placeholder="0.00"
                  className={`${inputCls} font-mono`}
                />
              </div>
            )}

            {/* Cash account — buy, sell, div, return_capital */}
            {(txType === 'buy' || txType === 'sell' || txType === 'div' || txType === 'return_capital') && (
              <div>
                <label className={labelCls}>{txType === 'buy' ? t('txForm.cashAccountFrom') : txType === 'sell' || txType === 'div' ? t('txForm.cashAccountTo') : t('txForm.cashAccount')}</label>
                <select
                  value={cashAccountId}
                  onChange={(e) => setCashAccountId(e.target.value)}
                  className="w-full bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                >
                  <option value="">{t('txForm.selectCashAccount')}</option>
                  {cashAccounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Capital gains account — sell */}
            {txType === 'sell' && (
              <div>
                <label className={labelCls}>{t('txForm.capitalGainsAccount')} <span className="text-text-subtle">{t('txForm.optional')}</span></label>
                <select
                  value={capGainsAccountId}
                  onChange={(e) => setCapGainsAccountId(e.target.value)}
                  className="w-full bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                >
                  <option value="">{t('txForm.useUnrealized')}</option>
                  {incomeAccounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                {estimatedGain !== null && (() => {
                  const termLabel = estimatedGain.term === 'long-term'
                    ? t('txForm.termLong')
                    : estimatedGain.term === 'short-term'
                    ? t('txForm.termShort')
                    : t('txForm.termMixed');
                  const amount = `${estimatedGain.gainLoss >= 0 ? '+' : ''}${(estimatedGain.gainLoss / 100).toFixed(2)}`;
                  return (
                    <p className={`text-xs mt-1 font-mono ${estimatedGain.gainLoss >= 0 ? 'text-positive' : 'text-negative'}`}>
                      {estimatedGain.gainLoss >= 0
                        ? t('txForm.estimatedGain', { term: termLabel, amount })
                        : t('txForm.estimatedLoss', { term: termLabel, amount })}
                    </p>
                  );
                })()}
              </div>
            )}

            {/* Income account — div and rdiv */}
            {(txType === 'div' || txType === 'rdiv') && (
              <div>
                <label className={labelCls}>{t('txForm.incomeAccount')}</label>
                <select
                  value={incomeAccountId}
                  onChange={(e) => setIncomeAccountId(e.target.value)}
                  className="w-full bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                >
                  <option value="">{t('txForm.selectIncomeAccount')}</option>
                  {incomeAccounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className={labelCls}>{t('txForm.memo')} <span className="text-text-subtle">{t('txForm.optional')}</span></label>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                className={inputCls}
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
              >
                {saving ? t('txForm.saving') : t('common:actions.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
